import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { WebSerialPrinterService } from 'app/core/services/webserial-printer.service';
import { WebUsbPrinterService } from 'app/core/services/webusb-printer.service';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PrinterAliasDialogComponent, PrinterAliasDialogData } from './printer-alias-dialog.component';
import { GrantedPort, PrinterDiscoveryService } from 'app/core/services/printer-discovery.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'settings-printers',
  templateUrl: './printers.component.html'
})
export class SettingsPrintersComponent implements OnInit, OnDestroy {
  // Transport selection
  transport: 'serial' | 'usb' = 'serial';
  supported = false; // serial support
  supportedUsb = false;

  loading = false;
  error?: string;
  success?: string;

  // Granted devices (via PrinterDiscoveryService)
  printers: GrantedPort[] = [];

  lastUsed: any;
  selectionMode = false;
  selectedCandidate?: any;
  isMobileResolution: boolean;

  private destroy$ = new Subject<void>();

  constructor(
    private webSerialPrinterService: WebSerialPrinterService,
    private webUsbPrinterService: WebUsbPrinterService,
    private translocoService: TranslocoService,
    private toast: ToastrService,
    private dialog: MatDialog,
    private printerDiscovery: PrinterDiscoveryService,
    @Optional() private dialogRef?: MatDialogRef<SettingsPrintersComponent, any>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data?: any
  ) {
    this.selectionMode = !!this.data?.selectionMode;
  }

  onTransportChange(): void {
    try {
      localStorage.setItem('printerTransport', this.transport);
    } catch {}
    this.supported = this.webSerialPrinterService.isSupported;
    this.supportedUsb = this.webUsbPrinterService.isSupported;
    this.lastUsed = this.transport === 'usb' ? this.webUsbPrinterService.lastDevice : this.webSerialPrinterService.lastDevice;
    this.printers = this.printerDiscovery.snapshot.filter(p => p.type === this.transport);
    this.refresh();
  }

  selectPrinter(p: GrantedPort): void {
    if (!this.selectionMode || !this.dialogRef) return;
    const adapted = {
      vendorId: p.info.usbVendorId,
      productId: p.info.usbProductId,
      transport: p.type || this.transport
    };
    this.dialogRef.close(adapted);
  }

  get supportedActive(): boolean {
    return this.transport === 'usb' ? this.supportedUsb : this.supported;
  }

  onCandidateChange(p: any): void {
    this.selectedCandidate = p;
  }

  approveSelection(): void {
    if (!this.selectionMode || !this.dialogRef || !this.selectedCandidate) return;
    const p = this.selectedCandidate;
    const adapted = {
      vendorId: p.info.usbVendorId,
      productId: p.info.usbProductId,
      transport: p.type || this.transport
    };
    this.dialogRef.close(adapted);
  }

  async testSelected(): Promise<void> {
    if (!this.selectedCandidate) {
      this.toast.error(this.translocoService.translate('Printers.messages.error.no_device_selected'));
      return;
    }
    const candidate = { vendorId: this.selectedCandidate.info.usbVendorId, productId: this.selectedCandidate.info.usbProductId };
    if (this.transport === 'usb') {
      const previous = this.webUsbPrinterService.lastDevice;
      try {
        this.webUsbPrinterService.setDefaultPrinter(candidate as any);
        await this.webUsbPrinterService.testPrint();
      } catch (e: any) {
        const msg = e?.message || this.translocoService.translate('Printers.messages.error.print_failed');
        this.toast.error(msg);
      } finally {
        this.webUsbPrinterService.setDefaultPrinter(previous || ({ vendorId: null, productId: null } as any));
        this.lastUsed = this.webUsbPrinterService.lastDevice;
      }
      return;
    }

    const previous = this.webSerialPrinterService.lastDevice;
    try {
      this.webSerialPrinterService.setDefaultPrinter(candidate);
      await this.webSerialPrinterService.testPrint();
    } catch (e: any) {
      const msg = e?.message || this.translocoService.translate('Printers.messages.error.print_failed');
      this.toast.error(msg);
    } finally {
      if (previous && (previous.vendorId != null || previous.productId != null)) {
        this.webSerialPrinterService.setDefaultPrinter(previous);
      } else {
        this.webSerialPrinterService.setDefaultPrinter({ vendorId: null, productId: null });
      }
      this.lastUsed = this.webSerialPrinterService.lastDevice;
    }
  }

  ngOnInit(): void {
    // restore last chosen transport
    const saved = localStorage.getItem('printerTransport') as 'serial' | 'usb' | null;
    if (saved === 'usb' || saved === 'serial') this.transport = saved;

    this.supported = this.webSerialPrinterService.isSupported;
    this.supportedUsb = this.webUsbPrinterService.isSupported;
    this.lastUsed = this.transport === 'usb' ? this.webUsbPrinterService.lastDevice : this.webSerialPrinterService.lastDevice;
    this.isMobileResolution = window.innerWidth < 720;

    this.printerDiscovery.printers$.pipe(takeUntil(this.destroy$)).subscribe(list => {
      this.printers = list.filter(p => p.type === this.transport);
    });

    this.refresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async refresh(): Promise<void> {
    this.loading = true;
    try {
      await this.printerDiscovery.refresh();
    } catch (e: any) {
      const msg = e?.message || this.translocoService.translate('Printers.messages.error.list_failed');
      this.toast.error(msg);
    } finally {
      this.loading = false;
    }
  }

  async addPrinterToGranted(): Promise<void> {
    try {
      if (this.transport === 'usb') {
        const before = await this.webUsbPrinterService.getGrantedDevices();
        await this.webUsbPrinterService.requestDevice();
        const after = await this.webUsbPrinterService.getGrantedDevices();
        const beforeSet = new Set(before.map(p => `${p.info.usbVendorId}:${p.info.usbProductId}`));
        const afterSet = new Set(after.map(p => `${p.info.usbVendorId}:${p.info.usbProductId}`));
        const hasNew = afterSet.size > beforeSet.size || [...afterSet].some(id => !beforeSet.has(id));
        if (!hasNew) this.toast.info(this.translocoService.translate('Printers.messages.info.permission_already_granted'));
        else this.toast.success(this.translocoService.translate('Printers.messages.success.permission_granted'));
      } else {
        const grantedPortsBefore = await this.webSerialPrinterService.getGrantedPorts();
        await this.webSerialPrinterService.requestPort();
        const grantedPortsAfter = await this.webSerialPrinterService.getGrantedPorts();
        const devicesBefore = new Set(grantedPortsBefore.map(p => `${p.info.usbVendorId}:${p.info.usbProductId}`));
        const devicesAfter = new Set(grantedPortsAfter.map(p => `${p.info.usbVendorId}:${p.info.usbProductId}`));
        const hasNewDevice = devicesAfter.size > devicesBefore.size || [...devicesAfter].some(device => !devicesBefore.has(device));
        if (!hasNewDevice) {
          this.toast.info(this.translocoService.translate('Printers.messages.info.permission_already_granted'));
        } else {
          this.toast.success(this.translocoService.translate('Printers.messages.success.permission_granted'));
        }
      }
      await this.printerDiscovery.refresh();
    } catch (e: any) {
      if (e && e.name === 'NotFoundError') {
        this.toast.error(this.translocoService.translate('Printers.messages.error.no_device_selected'));
      } else if (e && e.name === 'NotAllowedError') {
        this.toast.error(this.translocoService.translate('Printers.messages.error.permission_denied'));
      } else {
        const msg = e?.message || this.translocoService.translate('Printers.messages.error.request_device_failed');
        this.toast.error(msg);
      }
    }
  }

  connect(): void {
    try {
      if (this.transport === 'usb') {
        this.webUsbPrinterService.ensureOpen();
      } else {
        this.webSerialPrinterService.connect();
      }
    } catch (e) {
      // ignore
    }
  }

  async testPrint(): Promise<void> {
    if (this.transport === 'usb') {
      if (!this.webUsbPrinterService.lastDevice) {
        this.toast.error(this.translocoService.translate('Printers.messages.error.no_device_selected'));
        return;
      }
      try {
        await this.webUsbPrinterService.testPrint();
        this.toast.success(this.translocoService.translate('Printers.messages.success.test_print_sent'));
      } catch (e: any) {
        const msg = e?.message || this.translocoService.translate('Printers.messages.error.print_failed');
        this.toast.error(msg);
      }
      return;
    }

    if (!this.webSerialPrinterService.lastDevice) {
      this.toast.error(this.translocoService.translate('Printers.messages.error.no_device_selected'));
      return;
    }

    try {
      await this.webSerialPrinterService.testPrint();
      this.toast.success(this.translocoService.translate('Printers.messages.success.test_print_sent'));
    } catch (e: any) {
      const msg = e?.message || this.translocoService.translate('Printers.messages.error.print_failed');
      this.toast.error(msg);
    }
  }

  protected getPrinterName(p: any) {
    const adapted = {
      vendorId: p.info.usbVendorId,
      productId: p.info.usbProductId
    };
    return this.transport === 'usb'
      ? this.webUsbPrinterService.identifyPrinter({ ...adapted, productName: p.info.productName } as any)
      : this.webSerialPrinterService.identifyPrinter(adapted);
  }

  protected setDefault(p: any) {
    let adapted = {
      vendorId: p.info.usbVendorId,
      productId: p.info.usbProductId
    };

    if (this.transport === 'usb') {
      const last = this.webUsbPrinterService.lastDevice;
      if (last?.vendorId === adapted.vendorId && last?.productId === adapted.productId) {
        adapted = { vendorId: null, productId: null } as any;
      }
      this.webUsbPrinterService.setDefaultPrinter(adapted as any);
      this.lastUsed = this.webUsbPrinterService.lastDevice;
      if ((adapted as any).vendorId === null && (adapted as any).productId === null) {
        this.toast.info('Default printer removed');
        return;
      } else {
        this.toast.success('Default printer set to ' + this.getPrinterName(p));
      }
      return;
    }

    if (
      this.webSerialPrinterService.lastDevice?.vendorId === adapted.vendorId &&
      this.webSerialPrinterService.lastDevice?.productId === adapted.productId
    ) {
      adapted = { vendorId: null, productId: null };
    }

    this.webSerialPrinterService.setDefaultPrinter(adapted);
    this.lastUsed = this.webSerialPrinterService.lastDevice;
    if (adapted.vendorId === null && adapted.productId === null) {
      this.toast.info('Default printer removed');
      return;
    } else {
      this.toast.success('Default printer set to ' + this.getPrinterName(p));
    }
  }

  protected editAlias(p: any) {
    const adapted = {
      vendorId: p.info.usbVendorId,
      productId: p.info.usbProductId
    };

    const service = this.transport === 'usb' ? this.webUsbPrinterService : this.webSerialPrinterService;
    const current = service.getPrinterAlias(adapted) || '';

    const dialogRef = this.dialog.open<PrinterAliasDialogComponent, PrinterAliasDialogData, string | undefined>(
      PrinterAliasDialogComponent,
      {
        width: '400px',
        data: { currentAlias: current }
      }
    );

    dialogRef.afterClosed().subscribe(alias => {
      if (alias === undefined) return; // cancelled
      try {
        service.setPrinterAlias(adapted, alias);
        if ((alias || '').trim()) {
          this.toast.success(this.translocoService.translate('Printers.messages.success.alias_saved'));
        } else {
          this.toast.info(this.translocoService.translate('Printers.messages.success.alias_cleared'));
        }
      } catch (e: any) {
        this.toast.error(e?.message || this.translocoService.translate('Printers.messages.error.alias_update_failed'));
      }
    });
  }
}
